'use client';
import { Heading, Body } from '@depop/web-thrift-ui/Typography';
import { LockOutlineIcon } from '@depop/web-ui-kit/Icons/LockOutlineIcon';
import { LazyLoadImage } from '@depop/web-ui-kit/LazyLoadImage';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { DownloadAppButton } from '@/components/DownloadAppButton';
import {
  RQ_OUTFITS_KEY,
  RQ_OUTFITS_USER_CONFIG_KEY,
} from '@/modules/ReactQuery/cacheKeys';
import {
  getOutfitsUserConfig,
  getPaginatedOutfits,
} from '@/modules/outfits/api';
import { getNearestSizePictureFormat } from '@/modules/pictures/helpers';
import { type GetShopByUsernameResponse } from '@/modules/shop/aliases';

type Props = {
  seller: GetShopByUsernameResponse;
  shouldShowCarousel?: boolean;
};

const PREFERRED_IMAGE_SIZE = 320;

export function OutfitsModule({
  seller: { id, first_name: firstName },
  shouldShowCarousel = false,
}: Props) {
  const queryParams = useSearchParams();
  const utmCampaign = queryParams.get('utm_campaign');
  const utmMedium = queryParams.get('utm_medium');
  const isInfluencer =
    utmMedium?.toLowerCase().includes('influencer') ||
    utmCampaign?.toLowerCase().includes('influencer');
  const tCommon = useTranslations('common');
  const tOutfits = useTranslations('outfits');

  const { data: userConfigData } = useQuery({
    queryKey: [RQ_OUTFITS_USER_CONFIG_KEY],
    queryFn: () => getOutfitsUserConfig(String(id)),
  });

  const { data } = useInfiniteQuery({
    queryKey: [RQ_OUTFITS_KEY],
    queryFn: ({ pageParam }) =>
      getPaginatedOutfits({ limit: 4, after: pageParam, createdBy: id }),
    initialPageParam: '',
    getNextPageParam: () => void 0,
    enabled: userConfigData?.data.has_collages,
  });

  const shouldRender =
    !!data?.pages.length &&
    userConfigData?.data.has_collages &&
    (shouldShowCarousel || userConfigData?.data.show_carousel);

  if (!shouldRender) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.leftColumn}>
          <div className={styles.textWrapper}>
            <Heading variant="md">
              {tOutfits.rich('OutfitsBy', {
                firstName,
              })}
            </Heading>
            <Body variant="sm-regular" className={styles.subtitle}>
              {tOutfits.rich('DownloadTheApp', {
                firstName,
              })}
            </Body>
          </div>
          <DownloadAppButton
            as="span"
            excludeDeeplinkAttribution={isInfluencer}
            className={`${styles.downloadButton} ${styles.desktopButton}`}
            appPath={`/user/${id}?animated=true`}
            trackingId="outfits_module"
            label={tCommon('GetTheApp.Banner.Title')}
            desktopDeepView="depop_production_deepview_s1hd"
            canonicalUrl={window.location.href}
          />
        </div>
        <div className={styles.grid}>
          {data?.pages[0].data.objects.map(({ pictures, outfit_id }) => {
            const format = getNearestSizePictureFormat(
              Object.values(pictures[0].formats),
              PREFERRED_IMAGE_SIZE
            );
            return (
              <div key={outfit_id} className={styles.card}>
                <LazyLoadImage
                  alt=""
                  src={format?.url}
                  width={format?.width}
                  height={format?.height}
                  sizes="(max-width: 768px) 113px, 25vw"
                  className={styles.image}
                />
                <div className={styles.overlay}>
                  <LockOutlineIcon size={24} color="white" />
                </div>
              </div>
            );
          })}
        </div>
        <DownloadAppButton
          as="span"
          excludeDeeplinkAttribution={isInfluencer}
          className={`${styles.downloadButton} ${styles.mobileButton}`}
          appPath={`/user/${id}?animated=true`}
          trackingId="outfits_module"
          label={tCommon('GetTheApp.Banner.Title')}
          desktopDeepView="depop_production_deepview_s1hd"
          canonicalUrl={window.location.href}
        />
      </div>
    </div>
  );
}
